const { formatCurrency } = require('./helpers')

/**
 * @param {number} amountAsCents
 * @param {number} conversionRatePercent
 * @returns {number}
 */
function convertToPassengerCredits ({ amountAsCents, conversionRatePercent }) {
  if (!amountAsCents && amountAsCents !== 0) {
    throw new Error('"amount" required in converting to passenger credits')
  }

  if (!conversionRatePercent) {
    throw new Error('"conversionRatePercent" required in converting to passenger credits')
  }

  return Math.round(amountAsCents * (conversionRatePercent / 100))
}

/**
 * @param {number} amountAsCents
 * @param {number} conversionRatePercent
 * @returns {number}
 */
function convertToAgentCredits ({ amountAsCents, conversionRatePercent }) {
  if (!amountAsCents && amountAsCents !== 0) {
    throw new Error('"amount" required in converting to passenger credits')
  }

  if (!conversionRatePercent) {
    throw new Error('"conversionRatePercent" required in converting to passenger credits')
  }

  const conversionRateAsDivisor = parseFloat((conversionRatePercent / 100).toFixed(2))
  return Math.floor(amountAsCents / conversionRateAsDivisor)
}

/**
 * Float to put back on the agent when reversing a passenger top-up.
 * Uses convertToAgentCredits when a rate is present so the wallet gets back
 * what was deducted, not the passenger-credit face value.
 */
function getAgentFloatRestoreCents ({ amountAsCents, conversionRatePercent }) {
  const amount = Math.round(Number(amountAsCents) || 0)
  if (amount <= 0) {
    return 0
  }
  const rate = Number(conversionRatePercent)
  if (!rate) {
    return amount
  }
  return convertToAgentCredits({ amountAsCents: amount, conversionRatePercent: rate })
}

/**
 * Passenger credit minus convertToAgentCredits (float deducted). 0 when there is no gain.
 */
function getAgentTopupProfitCents ({ topupAmountAsCents, conversionRatePercent }) {
  const rate = Number(conversionRatePercent)
  const topupCents = Math.round(Number(topupAmountAsCents) || 0)
  if (!rate || topupCents <= 0) {
    return 0
  }
  const profitCents = topupCents - convertToAgentCredits({
    amountAsCents: topupCents,
    conversionRatePercent: rate
  })
  return profitCents > 0 ? profitCents : 0
}

function getAntiMoneyLaunderingFeePercent (conversionRatePercent) {
  const rate = Number(conversionRatePercent)
  if (!rate || rate <= 100) {
    return 0
  }
  return rate - 100
}

function getAntiMoneyLaunderingFee ({ refundAmountAsCents, conversionRatePercent }) {
  const amount = Number(refundAmountAsCents) || 0
  if (amount <= 0) {
    return 0
  }
  const feePercent = getAntiMoneyLaunderingFeePercent(conversionRatePercent)
  if (feePercent <= 0) {
    return 0
  }
  return Math.round(amount * feePercent / 100)
}

function deriveConversionRatePercentFromDeduction ({ passengerCreditAmount, agentFloatSpent }) {
  const passengerAmount = Number(passengerCreditAmount)
  const floatSpent = Number(agentFloatSpent)
  if (!passengerAmount || !floatSpent) {
    return null
  }
  return Math.round(passengerAmount / floatSpent * 100)
}

function resolveAgentTopUpRatePercent ({ conversionRatePercent, passengerCreditAmount, agentFloatSpent }) {
  const stored = Number(conversionRatePercent)
  if (stored) {
    return stored
  }
  return deriveConversionRatePercentFromDeduction({ passengerCreditAmount, agentFloatSpent }) || 100
}

function getMinimumRefundAmountForPositivePayout (conversionRatePercent) {
  const feePercent = getAntiMoneyLaunderingFeePercent(conversionRatePercent)
  if (feePercent <= 0) {
    return 1
  }
  if (feePercent >= 100) {
    return null
  }
  for (let refundAmount = 1; refundAmount <= 100000; refundAmount++) {
    if (refundAmount - getAntiMoneyLaunderingFee({
      refundAmountAsCents: refundAmount,
      conversionRatePercent: conversionRatePercent
    }) > 0) {
      return refundAmount
    }
  }
  return null
}

/**
 * Apply the AML fee to the agent-origin portion of a passenger refund (oldest slices first).
 * Self-purchase remainder has fee 0.
 *
 * @param {object} params
 * @param {number} params.refundAmountAsCents
 * @param {Array<{ remainingAmount: number, conversionRatePercent?: number, passengerCreditAmount?: number, agentFloatSpent?: number }>} params.slices
 * @returns {{ fee: number, momoPayout: number, agentOriginAllocated: number, slices: Array }}
 */
function allocateAgentTopUpRefundFee ({ refundAmountAsCents, slices }) {
  const refundAmount = Number(refundAmountAsCents) || 0
  let remaining = refundAmount
  let fee = 0
  const appliedSlices = []

  const sourceSlices = slices || []
  for (let i = 0; i < sourceSlices.length && remaining > 0; i++) {
    const slice = sourceSlices[i]
    const available = slice.remainingAmount > 0 ? slice.remainingAmount : 0
    const take = Math.min(remaining, available)
    if (take <= 0) {
      continue
    }

    const ratePercent = resolveAgentTopUpRatePercent({
      conversionRatePercent: slice.conversionRatePercent,
      passengerCreditAmount: slice.passengerCreditAmount,
      agentFloatSpent: slice.agentFloatSpent
    })
    const sliceFee = getAntiMoneyLaunderingFee({
      refundAmountAsCents: take,
      conversionRatePercent: ratePercent
    })
    fee += sliceFee
    appliedSlices.push({
      amount: take,
      conversionRatePercent: ratePercent,
      fee: sliceFee
    })
    remaining -= take
  }

  return {
    fee: fee,
    momoPayout: refundAmount - fee,
    agentOriginAllocated: refundAmount - remaining,
    slices: appliedSlices
  }
}

/**
 * Refund the destination phone's own MoMo credit first (no fee), then agent-origin
 * slices oldest first (AML fee applies).
 *
 * @param {object} params
 * @param {number} params.refundAmountAsCents
 * @param {number} params.phoneBoundRefundable - from computePhoneBoundRefundable for the destination phone
 * @param {Array} params.slices - from buildRemainingAgentOriginSlices
 * @returns {{ fee: number, momoPayout: number, agentOriginAllocated: number, slices: Array }}
 */
function allocatePhoneFirstRefundFee ({ refundAmountAsCents, phoneBoundRefundable, slices }) {
  const refundAmount = Number(refundAmountAsCents) || 0
  const phoneBound = Number(phoneBoundRefundable) || 0
  const fromPhone = Math.min(refundAmount, phoneBound > 0 ? phoneBound : 0)
  const agentPart = allocateAgentTopUpRefundFee({
    refundAmountAsCents: refundAmount - fromPhone,
    slices: slices
  })

  return {
    fee: agentPart.fee,
    momoPayout: refundAmount - agentPart.fee,
    agentOriginAllocated: agentPart.agentOriginAllocated,
    slices: agentPart.slices
  }
}

function buildZeroPayoutRefundError ({ fee, conversionRatePercent, currencyPrefix }) {
  const prefix = currencyPrefix || 'GHS'
  const feeLabel = formatCurrency(fee || 0, prefix)
  const zeroLabel = formatCurrency(0, prefix)
  let message = 'Refund cannot proceed: after the anti-money laundering fee of ' +
    feeLabel + ', ' + zeroLabel + ' would be sent to your mobile money account.'

  const feePercent = getAntiMoneyLaunderingFeePercent(conversionRatePercent)
  if (feePercent >= 100) {
    message += ' No amount of this credit can be refunded to mobile money.'
    return message
  }

  const minAmount = getMinimumRefundAmountForPositivePayout(conversionRatePercent)
  if (minAmount) {
    message += ' Refund at least ' + formatCurrency(minAmount, prefix) + '.'
  }
  return message
}

function buildRemainingAgentOriginSlices ({ credits, consumedAmount }) {
  let leftoverRefunded = Number(consumedAmount) || 0
  const remainingSlices = []
  const sourceCredits = credits || []

  for (let i = 0; i < sourceCredits.length; i++) {
    const credit = sourceCredits[i]
    const amount = Number(credit.amount) || 0
    if (amount <= 0) {
      continue
    }

    let remainingAmount = amount
    if (leftoverRefunded > 0) {
      const taken = Math.min(leftoverRefunded, amount)
      remainingAmount = amount - taken
      leftoverRefunded -= taken
    }

    if (remainingAmount <= 0) {
      continue
    }

    remainingSlices.push({
      remainingAmount: remainingAmount,
      conversionRatePercent: credit.conversionRatePercent,
      passengerCreditAmount: amount,
      agentFloatSpent: Number(credit.agentFloatSpent) || 0
    })
  }

  return remainingSlices
}

function sumRemainingAgentOrigin (slices) {
  let total = 0
  const sourceSlices = slices || []
  for (let i = 0; i < sourceSlices.length; i++) {
    total += Number(sourceSlices[i].remainingAmount) || 0
  }
  return total
}

/**
 * Self-MoMo credit still tied to a destination phone, after subtracting only the
 * portion of refunds to that phone that were not agent-origin (agent-origin can
 * be sent to any number).
 */
function computePhoneBoundRefundable ({ phonePurchasedCredit, phoneRefundsAbs, agentOriginAllocatedToPhone }) {
  const purchased = Number(phonePurchasedCredit) || 0
  const refunded = Number(phoneRefundsAbs) || 0
  const agentAllocated = Number(agentOriginAllocatedToPhone) || 0
  const selfRefunded = refunded > agentAllocated ? refunded - agentAllocated : 0
  const remaining = purchased - selfRefunded
  return remaining > 0 ? remaining : 0
}

function computePhonePermittedRefundAmount ({ overallRefundable, phoneBoundRefundable, remainingAgentOrigin }) {
  const overall = Number(overallRefundable) || 0
  const phoneBound = Number(phoneBoundRefundable) || 0
  const agentOrigin = Number(remainingAgentOrigin) || 0
  const combined = phoneBound + (agentOrigin > 0 ? agentOrigin : 0)
  return overall < combined ? overall : combined
}

/**
 * Unused agent float that may be sent to a destination MoMo phone.
 * Same-phone refunds are capped at what that phone still has outstanding.
 * Off-phone refunds use the overall unused amount when they are permitted.
 */
function computeAgentOwnCreditPermittedRefundAmount ({ overallRefundable, phoneBoundRefundable, offPhoneRefundsPermitted }) {
  const overall = Number(overallRefundable) || 0
  if (offPhoneRefundsPermitted) {
    return overall
  }
  return computePhonePermittedRefundAmount({
    overallRefundable: overall,
    phoneBoundRefundable: phoneBoundRefundable,
    remainingAgentOrigin: 0
  })
}

/**
 * How many average-sized agent top-ups are needed to hit a monthly profit target.
 * Profit per top-up is passenger credit minus convertToAgentCredits (the float deducted).
 */
function calculateAgentTopupProfit ({
  topupAmountAsCents,
  conversionRatePercent,
  targetProfitAsCents,
  workingDays
}) {
  const rate = Number(conversionRatePercent)
  const topupCents = Math.round(Number(topupAmountAsCents) || 0)
  const targetCents = Math.round(Number(targetProfitAsCents) || 0)
  const days = Math.round(Number(workingDays) || 0)

  if (!Number.isFinite(rate) || rate <= 100) {
    return null
  }
  if (topupCents <= 0 || targetCents <= 0 || days < 1) {
    return null
  }

  const agentSpendCents = convertToAgentCredits({
    amountAsCents: topupCents,
    conversionRatePercent: rate
  })
  const profitPerCents = topupCents - agentSpendCents
  if (profitPerCents <= 0) {
    return null
  }

  const monthlyTopUps = Math.ceil(targetCents / profitPerCents)
  return {
    topupCents: topupCents,
    agentSpendCents: agentSpendCents,
    profitPerCents: profitPerCents,
    monthlyTopUps: monthlyTopUps,
    dailyExact: monthlyTopUps / days,
    actualProfitCents: monthlyTopUps * profitPerCents,
    monthCreditCents: monthlyTopUps * topupCents,
    monthFloatCents: monthlyTopUps * agentSpendCents,
    workingDays: days
  }
}

module.exports = {
  convertToPassengerCredits,
  convertToAgentCredits,
  getAgentFloatRestoreCents,
  getAgentTopupProfitCents,
  getAntiMoneyLaunderingFeePercent,
  getAntiMoneyLaunderingFee,
  deriveConversionRatePercentFromDeduction,
  resolveAgentTopUpRatePercent,
  getMinimumRefundAmountForPositivePayout,
  allocateAgentTopUpRefundFee,
  allocatePhoneFirstRefundFee,
  buildRemainingAgentOriginSlices,
  sumRemainingAgentOrigin,
  computePhoneBoundRefundable,
  computePhonePermittedRefundAmount,
  computeAgentOwnCreditPermittedRefundAmount,
  buildZeroPayoutRefundError,
  calculateAgentTopupProfit
}
